/**
 * Rework Decision Modal
 *
 * Fires when an operator's time entry would extend a job past its recorded
 * completion timestamp at a station. Required yes/no question — NO default,
 * NO dismiss, NO "never ask again." Submit is disabled until the operator
 * explicitly chooses Yes or No.
 *
 * Caller pattern: submit op → catch HTTP 409 { code: 'REWORK_DECISION_REQUIRED' }
 * → open this modal with the response payload → resubmit with is_rework set.
 *
 * Esc / backdrop click cancels the underlying operation entirely
 * (dialogRef.close(undefined)). Callers must treat undefined as abort.
 */

import { Component, Inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

export interface ReworkDecisionModalData {
    station: string | null
    completedAt: string | null
    completedBy: string | null
}

export interface ReworkDecisionModalResult {
    isRework: boolean
}

@Component({
    selector: 'app-rework-decision-modal',
    templateUrl: './rework-decision-modal.component.html',
    styleUrls: ['./rework-decision-modal.component.scss'],
    standalone: false
})
export class ReworkDecisionModalComponent {
    /** Tri-state: null means the operator hasn't chosen yet — submit stays disabled. */
    answer: boolean | null = null

    constructor(
        public dialogRef: MatDialogRef<ReworkDecisionModalComponent, ReworkDecisionModalResult | undefined>,
        @Inject(MAT_DIALOG_DATA) public data: ReworkDecisionModalData
    ) {
        // Esc/backdrop are blocked by disableClose=true on dialog.open(); cancel
        // must go through the explicit Cancel button (which calls onCancel).
    }

    get stationLabel(): string {
        return this.data.station ? `at <strong>${this.data.station}</strong>` : 'at this station'
    }

    get formattedCompletedAt(): string {
        if (!this.data.completedAt) return ''
        try {
            return new Date(this.data.completedAt).toLocaleString()
        } catch {
            return this.data.completedAt
        }
    }

    select(value: boolean): void {
        this.answer = value
    }

    onSubmit(): void {
        if (this.answer === null) return
        this.dialogRef.close({ isRework: this.answer })
    }

    onCancel(): void {
        this.dialogRef.close(undefined)
    }
}
